import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { Person } from "@/components/shared/person";
import { cn } from "@/lib/utils";

interface PessoaPresenca {
  id: number;
  nome: string;
  email: string;
  foto?: string;
}

interface PersonPresenceListProps {
  icon: LucideIcon;
  iconClassName: string;
  title: string;
  pessoas: PessoaPresenca[];
  loading: boolean;
  emptyMessage: string;
  /** Repassado pro `Person.Root` de cada linha — só férias precisa forçar o selo 🌴. */
  ausente?: boolean;
  /** Badge à direita: `vazio` (ex. "time completo") ou `naoVazio` (ex. contador). */
  badge: { when: "vazio" | "naoVazio"; content: ReactNode };
}

/** Card "quem está em tal estado agora" — molde comum de `PessoasEmFeriasList`/`PessoasOnlineList`. */
export function PersonPresenceList({
  icon: Icon,
  iconClassName,
  title,
  pessoas,
  loading,
  emptyMessage,
  ausente = false,
  badge,
}: PersonPresenceListProps) {
  if (loading) return <Skeleton className="h-[68px] w-full rounded-xl" />;

  const vazio = pessoas.length === 0;
  const mostraBadge = badge.when === "vazio" ? vazio : !vazio;

  return (
    <div className="flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          iconClassName,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {vazio ? (
          <p className="text-xs text-muted-foreground">{emptyMessage}</p>
        ) : (
          <div className="mt-1.5 flex flex-wrap gap-3">
            {pessoas.map((pessoa) => (
              <Person.Root
                key={pessoa.id}
                pessoa={{ ...pessoa, ativo: true, ausente }}
                asClickable
                className="flex items-center gap-1.5"
              >
                <Person.Avatar />
                <Person.Name className="text-sm" />
              </Person.Root>
            ))}
          </div>
        )}
      </div>

      {mostraBadge && badge.content}
    </div>
  );
}
